import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";
import { tokenService } from "../../utils/tokenService";

interface StaffQuarterState {
    employeeDetails: any;
    requests: any;
    currentRequest: any;
    loading: "idle" | "pending" | "fulfilled" | "rejected";
    error: string | null;
    action: "get-employee-details" | "submit-registration" | "view-requests" | "get-details" | "approve-reject" | "withdraw-occupation" | "get-withdrawal-details" | "get-occupation-details" | "get-pending-approvals" | "get-current-registration" | "confirm-willingness" | "process-willingness-approval" | "get-occupancy-history" | null;
    pendingApprovals: any;
    financePendingApprovals: any;
    mutualExchangePendingApprovals: any;
    currentRegistration: any;
    occupationDetails: any;
    withdrawalDetails: any;
    locations: any;
    quarterTypes: any;
    pools: any;
    pendingWillingness: any;
    pendingUnwillingnessRequests: any;
    allVacations: any;
    vacationRequests: any;
    adminVacations: any;
    vacationIntimationData: any;
    vacationDetails: any;
    myAllotments: any;
    allAllotments: any;
    pendingVacations: any;
    occupancyHistory: any;
    permissions: any;
    adminPermissions: any;
    licenseTypes: any;
    isCurrentRegistrationLoaded: boolean;
}

const initialState: StaffQuarterState = {
    employeeDetails: null,
    requests: null,
    currentRequest: null,
    loading: "idle",
    error: null,
    action: null,
    locations: null,
    quarterTypes: null,
    pools: null,
    pendingWillingness: null,
    pendingUnwillingnessRequests: null,
    allVacations: null,
    vacationRequests: null,
    adminVacations: null,
    vacationIntimationData: null,
    vacationDetails: null,
    myAllotments: null,
    allAllotments: null,
    occupationDetails: null,
    withdrawalDetails: null,
    pendingApprovals: null,
    financePendingApprovals: null,
    mutualExchangePendingApprovals: null,
    currentRegistration: null,
    pendingVacations: null,
    occupancyHistory: null,
    permissions: null,
    adminPermissions: null,
    licenseTypes: null,
    isCurrentRegistrationLoaded: false,
};

export const getStaffQuarterPermissions: any = createAsyncThunk(
    "staffQuarter/getPermissions",
    async (payload: any, thunkAPI) => {
        // Stop calling the permissions API from the network; return an empty permissions list directly.
        return { data: { data: [] } };
    }
);

export const getStaffQuarterAdminPermissions: any = createAsyncThunk(
    "staffQuarter/getAdminPermissions",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.get(
                API_URLS.STAFF_QUARTER.ADMIN_ROLES_PERMISSIONS,
                {},
                true
            );
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getOccupancyHistory: any = createAsyncThunk(
    "staffQuarter/getOccupancyHistory",
    async (payload: any, thunkAPI) => {
        try {
            const _wrappedPayload_1 = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.get(API_URLS.STAFF_QUARTER.OCCUPANCY_HISTORY, _wrappedPayload_1, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getEmployeeDetailsForStaffQuarter: any = createAsyncThunk(
    "staffQuarter/getEmployeeDetails",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_EMPLOYEE_DETAILS,
                {
                    Header: {
                        device_type: "WEB"
                    }
                },
                true
            );
            return response?.data?.data || response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const submitStaffQuarterRegistration: any = createAsyncThunk(
    "staffQuarter/submitRegistration",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.REGISTER,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterRequests: any = createAsyncThunk(
    "staffQuarter/getRequests",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.VIEW,
                {
                    Header: {
                        device_type: "WEB"
                    }
                },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterDetails: any = createAsyncThunk(
    "staffQuarter/getDetails",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: {
                        id: payload.id
                    }
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_BY_ID,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const approveRejectStaffQuarter: any = createAsyncThunk(
    "staffQuarter/approveReject",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.APPROVE_REJECT,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const approveRejectStaffQuarterOccupation: any = createAsyncThunk(
    "staffQuarter/approveRejectOccupation",
    async (payload: any, thunkAPI) => {
        try {
            const _wrappedPayload_2 = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(API_URLS.STAFF_QUARTER.APPROVE_REJECT_OCCUPATION, _wrappedPayload_2, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const extendStaffQuarterOccupation: any = createAsyncThunk(
    "staffQuarter/extendOccupation",
    async (payload: any, thunkAPI) => {
        try {
            const _wrappedPayload_3 = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(API_URLS.STAFF_QUARTER.EXTEND_OCCUPATION, _wrappedPayload_3, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getUserStaffQuarterRequests: any = createAsyncThunk(
    "staffQuarter/getUserRequests",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_USER_REQUESTS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const submitStaffQuarterOccupation: any = createAsyncThunk(
    "staffQuarter/submitOccupation",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client"
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.CREATE_OCCUPATION,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterOccupations: any = createAsyncThunk(
    "staffQuarter/getOccupations",
    async (payload: any, thunkAPI) => {
        try {
            const _wrappedPayload_4 = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(API_URLS.STAFF_QUARTER.LIST_OCCUPATION, _wrappedPayload_4, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const submitStaffQuarterVacation: any = createAsyncThunk(
    "staffQuarter/submitVacation",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.CREATE_VACATION,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const addVacationIntimation: any = createAsyncThunk(
    "staffQuarter/addVacationIntimation",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.ADD_VACATION_INTIMATION,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getVacationIntimationData: any = createAsyncThunk(
    "staffQuarter/getVacationIntimationData",
    async (_payload: any, thunkAPI) => {
        try {
            const response = await Api.get(
                API_URLS.STAFF_QUARTER.GET_VACATION_INTIMATION_DATA,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getAllVacationIntimations: any = createAsyncThunk(
    "staffQuarter/getAllVacationIntimations",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.get(
                API_URLS.STAFF_QUARTER.GET_VACATION_INTIMATION_DATA,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            try {
                const wrappedPayload = {
                    Header: {
                        device_type: "WEB"
                    },
                    allotmentReferenceNumber: String(payload?.allotmentReferenceNumber || payload?.referenceNumber || "")
                };
                const fbRes = await Api.post(
                    API_URLS.STAFF_QUARTER.VACATION_HISTORY,
                    wrappedPayload,
                    true
                );
                return fbRes?.data || fbRes;
            } catch (err: any) {
                return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
            }
        }
    }
);

export const getVacationIntimationDetails: any = createAsyncThunk(
    "staffQuarter/getVacationDetails",
    async (payload: any, thunkAPI) => {
        try {
            if (payload?.empId) {
                return await Api.get(
                    `${API_URLS.STAFF_QUARTER.GET_VACATION_INTIMATION}/${encodeURIComponent(String(payload.empId))}`,
                    {},
                    true
                );
            }
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                allotmentReferenceNumber: String(payload?.allotmentReferenceNumber || payload?.id || "")
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_VACATION_INTIMATION, // Reusing list endpoint but it might return single object if filtered
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const approveRejectVacationIntimation: any = createAsyncThunk(
    "staffQuarter/approveRejectVacation",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.APPROVE_REJECT_VACATION,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getAdminVacationIntimationDetails: any = createAsyncThunk(
    "staffQuarter/getAdminVacationDetails",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                allotmentReferenceNumber: String(payload?.allotmentReferenceNumber || payload?.id || ""),
                vacationIntimationId: payload?.vacationIntimationId || payload?.id || ""
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_VACATION_INTIMATION,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getAdminVacationIntimations: any = createAsyncThunk(
    "staffQuarter/getAdminVacationIntimations",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                status: payload?.status || "PENDING"
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_ADMIN_VACATION_INTIMATIONS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getPendingVacationRequests: any = createAsyncThunk(
    "staffQuarter/getPendingVacationRequests",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: payload || {}
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_PENDING_VACATION_REQUESTS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterFinancePendingApprovals: any = createAsyncThunk(
    "staffQuarter/getFinancePendingApprovals",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload || {}
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.FINANCE_PENDING_APPROVALS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const approveRejectStaffQuarterFinance: any = createAsyncThunk(
    "staffQuarter/approveRejectFinance",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.FINANCE_APPROVE_REJECT,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterLicenseTypes: any = createAsyncThunk(
    "staffQuarter/getLicenseTypes",
    async (_payload: any, thunkAPI) => {
        try {
            let response: any;
            try {
                response = await Api.get(
                    API_URLS.STAFF_QUARTER.LICENSE_TYPES,
                    {},
                    true
                );
            } catch (getErr: any) {
                response = await Api.post(
                    API_URLS.STAFF_QUARTER.LICENSE_TYPES,
                    { Header: { device_type: "WEB" } },
                    true
                );
            }
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterOccupationDetails: any = createAsyncThunk(
    "staffQuarter/getOccupationDetails",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                registrationId: payload.registrationId || payload.referenceNumber
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_OCCUPATION_DETAILS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterCurrentRegistration: any = createAsyncThunk(
    "staffQuarter/getCurrentRegistration",
    async (payload: any, thunkAPI) => {
        const urlsToTry = [
            API_URLS.STAFF_QUARTER.GET_CURRENT_REGISTRATION_DETAILS,
            API_URLS.STAFF_QUARTER.GET_CURRENT_REGISTRATION,
            API_URLS.STAFF_QUARTER.GET_USER_REQUESTS
        ];

        const wrappedPayload = {
            Header: {
                device_type: "WEB",
                device_id: "WEB",
                "enc-req": false
            },
            Request: {
                data: payload || {}
            }
        };

        let lastErr = null;
        for (const url of urlsToTry) {
            try {
                const response = await Api.post(url, wrappedPayload, true);
                if (response?.data || response?.success) {
                    return response?.data || response;
                }
            } catch (error: any) {
                lastErr = error;
            }
        }
        return thunkAPI.rejectWithValue(Api.getErrorMessage(lastErr));
    }
);

export const withdrawStaffQuarterOccupation: any = createAsyncThunk(
    "staffQuarter/withdrawOccupation",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client"
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.WITHDRAW,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterWithdrawalDetails: any = createAsyncThunk(
    "staffQuarter/getWithdrawalDetails",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "WEB"
                },
                registrationId: payload.registrationId || payload.referenceNumber
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_WITHDRAWAL_DETAILS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterInventorySummary: any = createAsyncThunk(
    "staffQuarter/getInventorySummary",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.INVENTORY_SUMMARY,
                { Header: { device_type: "WEB" }, ...payload },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterAllFlats: any = createAsyncThunk(
    "staffQuarter/getAllFlats",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_ALL_FLATS,
                { Header: { device_type: "WEB" }, ...payload },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const createStaffQuarterFlat: any = createAsyncThunk(
    "staffQuarter/createFlat",
    async (payload: any, thunkAPI) => {
        try {
            const _wrappedPayload_5 = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(API_URLS.STAFF_QUARTER.CREATE_FLAT, _wrappedPayload_5, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const updateFlatPoolType: any = createAsyncThunk(
    "staffQuarter/updateFlatPoolType",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.UPDATE_POOL_TYPE,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterPriorityList: any = createAsyncThunk(
    "staffQuarter/getPriorityList",
    async (payload: any, thunkAPI) => {
        try {
            const rawPayload = payload || {};
            const qCode = rawPayload.quarterTypeCode ?? rawPayload.quarter_type_code;

            const finalPayload: any = {
                ...rawPayload
            };

            if (qCode !== undefined && qCode !== null && qCode !== "") {
                const numericCode = isNaN(Number(qCode)) ? qCode : Number(qCode);
                finalPayload.quarterTypeCode = numericCode;
                finalPayload.quarter_type_code = numericCode;
                if (!finalPayload.quarterType) {
                    finalPayload.quarterType = typeof numericCode === 'number' ? `Type-${numericCode}` : String(numericCode);
                }
            }

            if (rawPayload.locationId !== undefined && rawPayload.locationId !== null && rawPayload.locationId !== "") {
                finalPayload.locationId = isNaN(Number(rawPayload.locationId)) ? rawPayload.locationId : Number(rawPayload.locationId);
            }

            if (rawPayload.poolTypeId !== undefined && rawPayload.poolTypeId !== null && rawPayload.poolTypeId !== "") {
                finalPayload.poolTypeId = isNaN(Number(rawPayload.poolTypeId)) ? rawPayload.poolTypeId : Number(rawPayload.poolTypeId);
            }

            const response = await Api.post(API_URLS.STAFF_QUARTER.GET_PRIORITY_LIST, finalPayload, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterOccupationWithdrawalList: any = createAsyncThunk(
    "staffQuarter/getOccupationWithdrawalList",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_OCCUPATION_WITHDRAWAL_LIST,
                { Header: { device_type: "WEB" }, ...payload },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterPendingApprovals: any = createAsyncThunk(
    "staffQuarter/getPendingApprovals",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Request: {
                    data: payload || {}
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.PENDING_APPROVALS,
                wrappedPayload,
                true
            );
            return response?.data?.data || response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterRequestsByStatus: any = createAsyncThunk(
    "staffQuarter/getRequestsByStatus",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GET_REQUESTS_BY_STATUS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterLocations: any = createAsyncThunk(
    "staffQuarter/getLocations",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.LOCATIONS,
                { Header: { device_type: "WEB" } },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getPreferredLocations: any = createAsyncThunk(
    "staffQuarter/getPreferredLocations",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.PREFERRED_LOCATIONS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterQuarterTypes: any = createAsyncThunk(
    "staffQuarter/getQuarterTypes",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.QUARTER_TYPES,
                { Header: { device_type: "WEB" } },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterPools: any = createAsyncThunk(
    "staffQuarter/getPools",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.POOLS,
                { Header: { device_type: "WEB" } },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const generateStaffQuarterWillingness: any = createAsyncThunk(
    "staffQuarter/generateWillingness",
    async (payload: any, thunkAPI) => {
        try {
            const flatIds = payload.flatIds && Array.isArray(payload.flatIds) && payload.flatIds.length > 0
                ? payload.flatIds
                : [payload.flatId];

            // Client-side payload validation for atomic request
            const uniqueFlatIds = Array.from(new Set(flatIds));
            if (uniqueFlatIds.length !== flatIds.length) {
                return thunkAPI.rejectWithValue("Duplicate flat IDs provided in willingness generation request.");
            }

            const dataToSend = {
                ...payload,
                flatIds: uniqueFlatIds,
                flatId: uniqueFlatIds[0]
            };

            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: dataToSend
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.GENERATE_WILLINGNESS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const processWillingnessApproval: any = createAsyncThunk(
    "staffQuarter/processWillingnessApproval",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.PROCESS_WILLINGNESS_APPROVAL,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const processStaffQuarterUnwillingnessApproval: any = createAsyncThunk(
    "staffQuarter/processUnwillingnessApproval",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.PROCESS_UNWILLINGNESS_APPROVAL,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getStaffQuarterApprovalConfig: any = createAsyncThunk(
    "staffQuarter/getApprovalConfig",
    async (appId: string | number, thunkAPI) => {
        try {
            const response = await Api.get(
                `${API_URLS.STAFF_QUARTER.APPROVAL_CONFIG}?appId=${appId}`,
                null,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const saveStaffQuarterApprovalConfig: any = createAsyncThunk(
    "staffQuarter/saveApprovalConfig",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.APPROVAL_CONFIG,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getWillingnessApproversByPool: any = createAsyncThunk(
    "staffQuarter/getWillingnessApproversByPool",
    async (poolId: string | number, thunkAPI) => {
        try {
            const response = await Api.get(
                `${API_URLS.STAFF_QUARTER.WILLINGNESS_APPROVERS}?poolId=${poolId}`,
                null,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const updateApproverConfigByPool: any = createAsyncThunk(
    "staffQuarter/updateApproverConfigByPool",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.APPROVAL_CONFIG_POOL,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getLocationApprovers: any = createAsyncThunk(
    "staffQuarter/getLocationApprovers",
    async ({ appId, locationId }: { appId: string | number; locationId: string | number }, thunkAPI) => {
        try {
            const response = await Api.get(
                `${API_URLS.STAFF_QUARTER.LOCATION_APPROVERS}?appId=${appId}&locationId=${locationId}`,
                null,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const updateApproverConfigByLocation: any = createAsyncThunk(
    "staffQuarter/updateApproverConfigByLocation",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.LOCATION_APPROVERS,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getRegistrationApprovers: any = createAsyncThunk(
    "staffQuarter/getRegistrationApprovers",
    async (empId: string | number, thunkAPI) => {
        try {
            const response = await Api.get(
                `${API_URLS.STAFF_QUARTER.REGISTRATION_APPROVERS}?empId=${empId}`,
                null,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getMyAllotments: any = createAsyncThunk(
    "staffQuarter/getMyAllotments",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload || {}
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.MY_ALLOTMENTS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const confirmStaffQuarterWillingness: any = createAsyncThunk(
    "staffQuarter/confirmWillingness",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.CONFIRM_WILLINGNESS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const submitStaffQuarterUnwillingness: any = createAsyncThunk(
    "staffQuarter/submitUnwillingness",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.SUBMIT_UNWILLINGNESS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getAllAllotments: any = createAsyncThunk(
    "staffQuarter/getAllAllotments",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.ALL_ALLOTMENTS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getAllWillingness: any = createAsyncThunk(
    "staffQuarter/getAllWillingness",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                page: payload?.page || 1,
                limit: payload?.limit || 100,
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.ALL_WILLINGNESS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getPendingWillingnessRequests: any = createAsyncThunk(
    "staffQuarter/getPendingWillingness",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                }
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.PENDING_WILLINGNESS_REQUESTS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getPendingUnwillingnessRequests: any = createAsyncThunk(
    "staffQuarter/getPendingUnwillingnessRequests",
    async (payload: any, thunkAPI) => {
        try {
            const wrappedPayload = {
                Header: {
                    device_type: "web",
                    device_id: "web-client",
                    "enc-req": false
                },
                Request: {
                    data: payload
                },
                ...payload
            };
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.PENDING_UNWILLINGNESS_REQUESTS,
                wrappedPayload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const allotStaffQuarterFlat: any = createAsyncThunk(
    "staffQuarter/allotFlat",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.ALLOT_FLAT + "allot",
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getAllFlatsForStaffQuarter: any = createAsyncThunk(
    "staffQuarter/getAllFlats",
    async (payload: any, thunkAPI) => {
        try {
            const body: any = { Header: { device_type: "WEB" } };
            if (payload?.location) body.location = payload.location;
            const _wrappedPayload_7 = {
                Header: {
                    device_type: "WEB",
                    device_id: "WEB",
                    "enc-req": false
                },
                Request: {
                    data: body
                }
            };
            const response = await Api.post(API_URLS.STAFF_QUARTER.GET_ALL_FLATS, _wrappedPayload_7, true);
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const allotFlatToEmployee: any = createAsyncThunk(
    "staffQuarter/allotFlatToEmployee",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.get(
                API_URLS.STAFF_QUARTER.ALLOT_FLAT + payload.flatNumber,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const exportStaffQuarterPriorityList: any = createAsyncThunk(
    "staffQuarter/exportPriorityList",
    async (payload: any, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                url: API_URLS.STAFF_QUARTER.EXPORT_PRIORITY_LIST,
                method: 'POST',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    Header: {
                        device_type: "WEB"
                    }
                }
            });
            return response?.data?.data || response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const exportStaffQuarterOccupancyReport: any = createAsyncThunk(
    "staffQuarter/exportOccupancyReport",
    async (payload: any, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                url: API_URLS.STAFF_QUARTER.EXPORT_OCCUPANCY_REPORT,
                method: 'POST',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    Header: {
                        device_type: "WEB"
                    }
                }
            });
            return response?.data?.data || response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const downloadAllotmentLetter: any = createAsyncThunk(
    "staffQuarter/downloadAllotmentLetter",
    async (payload: { registrationId: number | string }, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                url: API_URLS.STAFF_QUARTER.GENERATE_ALLOTMENT_LETTER,
                method: 'POST',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    Request: {
                        data: {
                            registrationId: Number(payload.registrationId)
                        }
                    }
                }
            });
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const downloadVacationLetter: any = createAsyncThunk(
    "staffQuarter/downloadVacationLetter",
    async (payload: { vacationId?: number | string; registrationId?: number | string; id?: number | string }, thunkAPI) => {
        const token = tokenService.getToken();
        const vacId = payload.vacationId || payload.id || 62;
        const regId = payload.registrationId || 2613;

        try {
            const response = await axios({
                url: API_URLS.STAFF_QUARTER.GENERATE_VACATION_LETTER,
                method: 'POST',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    vacationId: Number(vacId),
                    registrationId: Number(regId)
                }
            });
            if (response && (response.status === 200 || response.status === 201) && response.data) {
                return response.data;
            }
            return thunkAPI.rejectWithValue("Failed to generate vacation letter PDF.");
        } catch (err: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(err));
        }
    }
);

export const getVacationLetterData: any = createAsyncThunk(
    "staffQuarter/getVacationLetterData",
    async (payload: { vacationId: number | string }, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.VACATION_LETTER_DATA,
                { vacationId: Number(payload.vacationId) },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const generateDeclarationPdf: any = createAsyncThunk(
    "staffQuarter/generateDeclarationPdf",
    async (payload: { registrationId: number | string }, thunkAPI) => {
        const token = tokenService.getToken();
        const urlsToTry = [
            API_URLS.STAFF_QUARTER.GENERATE_DECLARATION_PDF,
            API_URLS.STAFF_QUARTER.GENERATE_DECLARATION_PDF.replace("staff-quarters-occupation", "staff-quarter-occupation"),
            API_URLS.STAFF_QUARTER.GENERATE_DECLARATION_PDF.replace("staff-quarters-occupation", "staff-quarters")
        ];

        let lastErr = null;
        for (const url of urlsToTry) {
            try {
                const response = await axios({
                    url,
                    method: 'POST',
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    data: {
                        registrationId: Number(payload.registrationId || 1)
                    }
                });
                if (response && (response.status === 200 || response.status === 201)) {
                    return response.data;
                }
            } catch (error: any) {
                lastErr = error;
            }
        }
        return thunkAPI.rejectWithValue(Api.getErrorMessage(lastErr));
    }
);

export const generateOccupationReportPdf: any = createAsyncThunk(
    "staffQuarter/generateOccupationReportPdf",
    async (payload: { registrationId: number | string }, thunkAPI) => {
        const token = tokenService.getToken();
        const urlsToTry = [
            API_URLS.STAFF_QUARTER.GENERATE_OCCUPATION_REPORT_PDF,
            API_URLS.STAFF_QUARTER.GENERATE_OCCUPATION_REPORT_PDF.replace("staff-quarters-occupation", "staff-quarter-occupation"),
            API_URLS.STAFF_QUARTER.GENERATE_OCCUPATION_REPORT_PDF.replace("staff-quarters-occupation", "staff-quarters")
        ];

        let lastErr = null;
        for (const url of urlsToTry) {
            try {
                const response = await axios({
                    url,
                    method: 'POST',
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    data: {
                        registrationId: Number(payload.registrationId || 1)
                    }
                });
                if (response && (response.status === 200 || response.status === 201)) {
                    return response.data;
                }
            } catch (error: any) {
                lastErr = error;
            }
        }
        return thunkAPI.rejectWithValue(Api.getErrorMessage(lastErr));
    }
);

export const uploadDeclarationSignature: any = createAsyncThunk(
    "staffQuarter/uploadDeclarationSignature",
    async (payload: { registrationId?: number | string; occupationId?: number | string; signature: string; fileName: string }, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.UPLOAD_DECLARATION_SIGNATURE,
                payload,
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const updateLocationPreferences: any = createAsyncThunk(
    "staffQuarter/updateLocationPreferences",
    async (payload: {
        registrationId: number | string;
        locationPreference1Id: number | string;
        locationPreference2Id: number | string;
        locationPreference3Id: number | string;
    }, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                url: API_URLS.STAFF_QUARTER.UPDATE_LOCATION,
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: {
                    registrationId: Number(payload.registrationId),
                    locationPreference1Id: Number(payload.locationPreference1Id),
                    locationPreference2Id: payload.locationPreference2Id ? Number(payload.locationPreference2Id) : null,
                    locationPreference3Id: payload.locationPreference3Id ? Number(payload.locationPreference3Id) : null
                }
            });

            // Check if backend returned success: false, even with status 2xx
            if (response?.data?.success === false) {
                return {
                    success: false,
                    message: response?.data?.message || "Failed to update location preferences."
                };
            }

            return {
                success: true,
                message: response?.data?.message || "Location preferences updated successfully!"
            };
        } catch (error: any) {
            return {
                success: false,
                message: Api.getErrorMessage(error) || "An error occurred while updating preferences."
            };
        }
    }
);

export const updateStaffQuarterCurrentApprover: any = createAsyncThunk(
    "staffQuarter/updateCurrentApprover",
    async (payload: {
        module: "REGISTRATION" | "WILLINGNESS" | "OCCUPATION" | "VACATION";
        registrationId?: number | string;
        willingnessId?: number | string;
        occupationId?: number | string;
        vacationId?: number | string;
        newApproverEmpId: string;
        remarks?: string;
    }, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            let url = API_URLS.STAFF_QUARTER.ADD_UPDATE_APPROVER;
            let body: any = {
                newApproverEmpId: payload.newApproverEmpId,
            };

            if (payload.remarks) {
                body.remarks = payload.remarks;
            }

            if (payload.module === "REGISTRATION") {
                url = API_URLS.STAFF_QUARTER.ADD_UPDATE_APPROVER;
                body.module = "REGISTRATION";
                if (payload.registrationId) body.registrationId = Number(payload.registrationId);
            } else if (payload.module === "WILLINGNESS") {
                url = API_URLS.STAFF_QUARTER.ADD_UPDATE_APPROVER;
                body.module = "WILLINGNESS";
                if (payload.registrationId) body.registrationId = Number(payload.registrationId);
                if (payload.willingnessId) body.willingnessId = Number(payload.willingnessId);
            } else if (payload.module === "OCCUPATION") {
                url = API_URLS.STAFF_QUARTER.OCCUPATION_ADD_UPDATE_APPROVER;
                if (payload.registrationId) body.registrationId = Number(payload.registrationId);
                if (payload.occupationId) body.occupationId = Number(payload.occupationId);
            } else if (payload.module === "VACATION") {
                url = API_URLS.STAFF_QUARTER.VACATION_ADD_UPDATE_APPROVER;
                if (payload.registrationId) body.registrationId = Number(payload.registrationId);
                if (payload.vacationId) body.vacationId = Number(payload.vacationId);
            }

            const response = await axios({
                method: "POST",
                url: url,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: body
            });

            const resData = response?.data || response;
            if (resData?.success === false) {
                return thunkAPI.rejectWithValue(resData?.message || "Failed to update current approver.");
            }

            return resData;
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || Api.getErrorMessage(error) || "An error occurred while updating approver.";
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const getStaffQuarterCurrentPendingRequests: any = createAsyncThunk(
    "staffQuarter/getCurrentPendingRequests",
    async (payload: { empId: string }, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTER.CURRENT_PENDING_REQUESTS,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: {
                    empId: payload.empId
                }
            });

            return response?.data || response;
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to fetch current pending requests.";
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const getMutualExchangeEmployeeQuarterInfo: any = createAsyncThunk(
    "staffQuarter/getMutualExchangeEmployeeQuarterInfo",
    async (payload: { empId: string; compareEmpId?: string }, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const body: any = {
                empId: payload.empId
            };
            if (payload.compareEmpId) {
                body.compareEmpId = payload.compareEmpId;
            }
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.EMPLOYEE_QUARTER_INFO,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: body
            });

            return response?.data || response;
        } catch (error: any) {
            const errorData = error?.response?.data || error;
            return thunkAPI.rejectWithValue(errorData);
        }
    }
);

export const createMutualExchange: any = createAsyncThunk(
    "staffQuarter/createMutualExchange",
    async (payload: any, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.CREATE,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: payload
            });

            return response?.data || response;
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to create mutual exchange application.";
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const getAdminMutualExchangeRequests = createAsyncThunk(
    "staffQuarter/getAdminMutualExchangeRequests",
    async (payload: { status: string; page: number; limit: number; search?: string; location?: string; quarterType?: string; fromDate?: string; toDate?: string }, thunkAPI) => {
        try {
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.ADMIN_REQUESTS,
                headers: {
                    Authorization: `Bearer ${tokenService.getToken()}`,
                    "Content-Type": "application/json"
                },
                data: payload,
                signal: thunkAPI.signal
            });
            if (response.data?.success === false) {
                return thunkAPI.rejectWithValue(response.data.message || "Failed to fetch mutual exchange requests.");
            }
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to fetch mutual exchange requests.");
        }
    }
);

export const getVacationRequests: any = createAsyncThunk(
    "staffQuarter/getVacationRequests",
    async (payload: any, thunkAPI) => {
        try {
            const response = await Api.post(
                API_URLS.STAFF_QUARTER.VACATION_HISTORY,
                {
                    Header: { device_type: "WEB" },
                    allotmentReferenceNumber: String(payload?.allotmentReferenceNumber || payload?.referenceNumber || ""),
                    vacationId: payload?.vacationId || payload?.id || undefined
                },
                true
            );
            return response?.data || response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
        }
    }
);

export const getMutualExchangesList: any = createAsyncThunk(
    "staffQuarter/getMutualExchangesList",
    async (payload: any, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.FIND_ALL,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: payload || {}
            });

            return response?.data || response;
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to fetch mutual exchange applications.";
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const getMutualExchangeEmployeeExchanges: any = createAsyncThunk(
    "staffQuarter/getMutualExchangeEmployeeExchanges",
    async (payload: { empId: string; status?: string }, thunkAPI) => {
        try {
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.EMPLOYEE_EXCHANGES,
                headers: {
                    Authorization: `Bearer ${tokenService.getToken()}`,
                    "Content-Type": "application/json"
                },
                data: payload
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to fetch employee mutual exchanges."
            );
        }
    }
);

export const getMutualExchangePendingApprovals: any = createAsyncThunk(
    "staffQuarter/getMutualExchangePendingApprovals",
    async (payload: any, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.PENDING_APPROVALS,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: payload || {}
            });

            return response?.data || response;
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to fetch pending mutual exchange approvals.";
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

export const approveRejectMutualExchange: any = createAsyncThunk(
    "staffQuarter/approveRejectMutualExchange",
    async (payload: { id?: number | string; approvalId?: number | string; mutualExchangeId?: number | string; action: "APPROVED" | "REJECTED" | "APPROVE" | "REJECT"; remarks?: string }, thunkAPI) => {
        try {
            const token = tokenService.getToken();
            const actionFormatted = payload.action === "APPROVE" ? "APPROVED" : payload.action === "REJECT" ? "REJECTED" : payload.action;
            const dataObj: any = {
                id: payload.id ? Number(payload.id) : (payload.approvalId ? Number(payload.approvalId) : 1),
                approvalId: payload.approvalId ? Number(payload.approvalId) : (payload.id ? Number(payload.id) : 1),
                mutualExchangeId: payload.mutualExchangeId ? Number(payload.mutualExchangeId) : (payload.id ? Number(payload.id) : 1),
                action: actionFormatted,
                remarks: payload.remarks || (actionFormatted === "APPROVED" ? "Recommended for quarter exchange" : "Rejected")
            };

            const wrappedBody = {
                Header: {
                    device_type: "WEB"
                },
                Request: {
                    data: dataObj
                }
            };

            const response = await axios({
                method: "POST",
                url: API_URLS.STAFF_QUARTERS_MUTUAL_EXCHANGE.APPROVE_REJECT,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
                data: wrappedBody
            });

            return response?.data || response;
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || Api.getErrorMessage(error) || "Failed to process mutual exchange request.";
            return thunkAPI.rejectWithValue(errorMsg);
        }
    }
);

const staffQuarterSlice = createSlice({
    name: "staffQuarter",
    initialState,
    reducers: {
        resetStaffQuarter: (state: any) => {
            state.loading = "idle";
            state.error = null;
            state.employeeDetails = null;
            state.requests = null;
            state.currentRequest = null;
            state.action = null;
            state.pools = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getStaffQuarterRequests.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.requests = null;
            state.action = "view-requests";
        });
        builder.addCase(getStaffQuarterRequests.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.requests = action.payload;
            state.error = null;
            state.action = "view-requests";
        });
        builder.addCase(getStaffQuarterRequests.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.requests = null;
            state.action = "view-requests";
        });

        builder.addCase(getEmployeeDetailsForStaffQuarter.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.employeeDetails = null;
            state.action = "get-employee-details";
        });
        builder.addCase(
            getEmployeeDetailsForStaffQuarter.fulfilled,
            (state, action) => {
                state.loading = "fulfilled";
                state.employeeDetails = action.payload;
                state.error = null;
                state.action = "get-employee-details";
            }
        );
        builder.addCase(
            getEmployeeDetailsForStaffQuarter.rejected,
            (state, action) => {
                state.loading = "rejected";
                state.error = action.payload as string;
                state.employeeDetails = null;
                state.action = "get-employee-details";
            }
        );

        builder.addCase(getStaffQuarterDetails.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.currentRequest = null;
            state.action = "get-details";
        });
        builder.addCase(getStaffQuarterDetails.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.currentRequest = action.payload;
            state.error = null;
            state.action = "get-details";
        });
        builder.addCase(getStaffQuarterDetails.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.currentRequest = null;
            state.action = "get-details";
        });

        builder.addCase(approveRejectStaffQuarter.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "approve-reject";
        });
        builder.addCase(approveRejectStaffQuarter.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "approve-reject";
        });
        builder.addCase(approveRejectStaffQuarter.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "approve-reject";
        });

        builder.addCase(getStaffQuarterRequestsByStatus.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.requests = null;
            state.action = "view-requests";
        });
        builder.addCase(getStaffQuarterRequestsByStatus.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.requests = action.payload;
            state.error = null;
            state.action = "view-requests";
        });
        builder.addCase(getStaffQuarterRequestsByStatus.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.requests = null;
            state.action = "view-requests";
        });

        builder.addCase(getStaffQuarterOccupationDetails.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.occupationDetails = null;
            state.action = "get-occupation-details";
        });
        builder.addCase(getStaffQuarterOccupationDetails.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.occupationDetails = action.payload;
            state.error = null;
            state.action = "get-occupation-details";
        });
        builder.addCase(getStaffQuarterOccupationDetails.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.occupationDetails = null;
            state.action = "get-occupation-details";
        });

        builder.addCase(getStaffQuarterWithdrawalDetails.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.withdrawalDetails = null;
            state.action = "get-withdrawal-details";
        });
        builder.addCase(getStaffQuarterWithdrawalDetails.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.withdrawalDetails = action.payload;
            state.error = null;
            state.action = "get-withdrawal-details";
        });
        builder.addCase(getStaffQuarterWithdrawalDetails.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.withdrawalDetails = null;
            state.action = "get-withdrawal-details";
        });

        builder.addCase(getStaffQuarterCurrentRegistration.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.currentRegistration = null;
            state.action = "get-current-registration";
        });
        builder.addCase(getStaffQuarterCurrentRegistration.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.currentRegistration = action.payload;
            state.error = null;
            state.action = "get-current-registration";
            state.isCurrentRegistrationLoaded = true;
        });
        builder.addCase(getStaffQuarterCurrentRegistration.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.currentRegistration = null;
            state.action = "get-current-registration";
            state.isCurrentRegistrationLoaded = true;
        });

        builder.addCase(getStaffQuarterPendingApprovals.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.pendingApprovals = null;
            state.action = "get-pending-approvals";
        });
        builder.addCase(getStaffQuarterPendingApprovals.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.pendingApprovals = action.payload;
            state.error = null;
            state.action = "get-pending-approvals";
        });
        builder.addCase(getStaffQuarterPendingApprovals.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.pendingApprovals = null;
            state.action = "get-pending-approvals";
        });

        builder.addCase(submitStaffQuarterOccupation.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "submit-registration"; // Reusing action type or could add new one
        });
        builder.addCase(submitStaffQuarterOccupation.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "submit-registration";
        });
        builder.addCase(submitStaffQuarterOccupation.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "submit-registration";
        });

        builder.addCase(getUserStaffQuarterRequests.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.requests = null;
            state.action = "view-requests";
        });
        builder.addCase(getUserStaffQuarterRequests.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.requests = action.payload;
            state.error = null;
            state.action = "view-requests";
        });
        builder.addCase(getUserStaffQuarterRequests.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.requests = null;
            state.action = "view-requests";
        });

        builder.addCase(withdrawStaffQuarterOccupation.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "withdraw-occupation";
        });
        builder.addCase(withdrawStaffQuarterOccupation.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "withdraw-occupation";
        });
        builder.addCase(withdrawStaffQuarterOccupation.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "withdraw-occupation";
        });



        builder.addCase(getStaffQuarterLocations.fulfilled, (state, action) => {
            state.locations = action.payload;
        });
        builder.addCase(getStaffQuarterQuarterTypes.fulfilled, (state, action) => {
            state.quarterTypes = action.payload;
        });
        builder.addCase(getStaffQuarterPools.fulfilled, (state, action) => {
            state.pools = action.payload;
        });

        builder.addCase(confirmStaffQuarterWillingness.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "confirm-willingness";
        });
        builder.addCase(confirmStaffQuarterWillingness.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "confirm-willingness";
        });
        builder.addCase(confirmStaffQuarterWillingness.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "confirm-willingness";
        });

        builder.addCase(submitStaffQuarterUnwillingness.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "confirm-willingness";
        });
        builder.addCase(submitStaffQuarterUnwillingness.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "confirm-willingness";
        });
        builder.addCase(submitStaffQuarterUnwillingness.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "confirm-willingness";
        });

        builder.addCase(processWillingnessApproval.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "process-willingness-approval";
        });
        builder.addCase(processWillingnessApproval.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "process-willingness-approval";
        });
        builder.addCase(processWillingnessApproval.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "process-willingness-approval";
        });

        builder.addCase(processStaffQuarterUnwillingnessApproval.pending, (state) => {
            state.loading = "pending";
            state.error = null;
            state.action = "process-willingness-approval";
        });
        builder.addCase(processStaffQuarterUnwillingnessApproval.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
            state.action = "process-willingness-approval";
        });
        builder.addCase(processStaffQuarterUnwillingnessApproval.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "process-willingness-approval";
        });

        builder
            .addCase(getPendingWillingnessRequests.pending, (state) => {
                state.loading = "pending";
            })
            .addCase(getPendingWillingnessRequests.fulfilled, (state, action) => {
                state.loading = "fulfilled";
                state.pendingWillingness = action.payload;
            })
            .addCase(getPendingWillingnessRequests.rejected, (state, action) => {
                state.loading = "rejected";
                state.error = action.payload as string;
            })
            .addCase(getPendingUnwillingnessRequests.pending, (state) => {
                state.loading = "pending";
            })
            .addCase(getPendingUnwillingnessRequests.fulfilled, (state, action) => {
                state.loading = "fulfilled";
                state.pendingUnwillingnessRequests = action.payload;
            })
            .addCase(getPendingUnwillingnessRequests.rejected, (state, action) => {
                state.loading = "rejected";
                state.error = action.payload as string;
            });
        builder.addCase(getMyAllotments.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getMyAllotments.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.myAllotments = action.payload;
            state.error = null;
        });
        builder.addCase(getMyAllotments.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getAllAllotments.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getAllAllotments.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.allAllotments = action.payload;
            state.error = null;
        });
        builder.addCase(getAllAllotments.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getAllVacationIntimations.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getAllVacationIntimations.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.allVacations = action.payload;
            state.vacationIntimationData = action.payload;
            state.error = null;
        });
        builder.addCase(getAllVacationIntimations.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getVacationRequests.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getVacationRequests.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.vacationRequests = action.payload;
            state.error = null;
        });
        builder.addCase(getVacationRequests.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getVacationIntimationData.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getVacationIntimationData.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.allVacations = action.payload;
            state.vacationIntimationData = action.payload;
            state.error = null;
        });
        builder.addCase(getVacationIntimationData.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getAdminVacationIntimations.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getAdminVacationIntimations.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.adminVacations = action.payload;
            state.error = null;
        });
        builder.addCase(getAdminVacationIntimations.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getPendingVacationRequests.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getPendingVacationRequests.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.pendingVacations = action.payload;
            state.error = null;
        });
        builder.addCase(getPendingVacationRequests.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getStaffQuarterFinancePendingApprovals.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getStaffQuarterFinancePendingApprovals.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.financePendingApprovals = action.payload;
            state.error = null;
        });
        builder.addCase(getStaffQuarterFinancePendingApprovals.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(approveRejectStaffQuarterFinance.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(approveRejectStaffQuarterFinance.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
        });
        builder.addCase(approveRejectStaffQuarterFinance.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getStaffQuarterLicenseTypes.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getStaffQuarterLicenseTypes.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.licenseTypes = action.payload;
            state.error = null;
        });
        builder.addCase(getStaffQuarterLicenseTypes.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getVacationIntimationDetails.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getVacationIntimationDetails.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.vacationDetails = action.payload;
            state.error = null;
        });
        builder.addCase(getVacationIntimationDetails.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getAdminVacationIntimationDetails.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getAdminVacationIntimationDetails.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.vacationDetails = action.payload;
            state.error = null;
        });
        builder.addCase(getAdminVacationIntimationDetails.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getOccupancyHistory.pending, (state) => {
            state.loading = "pending";
            state.action = "get-occupancy-history";
            state.error = null;
        });
        builder.addCase(getOccupancyHistory.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.occupancyHistory = action.payload;
            state.action = "get-occupancy-history";
            state.error = null;
        });
        builder.addCase(getOccupancyHistory.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
            state.action = "get-occupancy-history";
        });

        builder.addCase(approveRejectVacationIntimation.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(approveRejectVacationIntimation.fulfilled, (state) => {
            state.loading = "fulfilled";
            state.error = null;
        });
        builder.addCase(approveRejectVacationIntimation.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getStaffQuarterPermissions.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getStaffQuarterPermissions.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.permissions = action.payload?.data?.data || action.payload?.data || action.payload || [];
            state.error = null;
        });
        builder.addCase(getStaffQuarterPermissions.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });

        builder.addCase(getStaffQuarterAdminPermissions.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getStaffQuarterAdminPermissions.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.adminPermissions = action.payload?.data?.data || action.payload?.data || action.payload || [];
            state.error = null;
        });
        builder.addCase(getStaffQuarterAdminPermissions.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });
        builder.addCase(getMutualExchangePendingApprovals.pending, (state) => {
            state.loading = "pending";
            state.error = null;
        });
        builder.addCase(getMutualExchangePendingApprovals.fulfilled, (state, action) => {
            state.loading = "fulfilled";
            state.mutualExchangePendingApprovals = action.payload;
            state.error = null;
        });
        builder.addCase(getMutualExchangePendingApprovals.rejected, (state, action) => {
            state.loading = "rejected";
            state.error = action.payload as string;
        });
        builder.addCase(submitStaffQuarterRegistration.fulfilled, (state) => {
            state.isCurrentRegistrationLoaded = false;
        });
    },
});

export const { resetStaffQuarter } = staffQuarterSlice.actions;
export default staffQuarterSlice.reducer;
